<template>
  <!-- Raised only when a new worker has installed and parked (pwa/appUpdate.ts).
       It sits at the BOTTOM: a module owns the top of the screen, and a bar
       dropped over a module's own header covers the thing the person is
       working on. Above the iOS home indicator via safe-area inset. -->
  <Transition name="upd">
    <div v-if="appUpdate.ready.value" class="upd" role="status">
      <span class="upd__text">{{ t("update.available") }}</span>
      <div class="upd__actions">
        <button type="button" class="upd__later" @click="dismissUpdate">{{ t("update.later") }}</button>
        <button type="button" class="upd__apply" @click="applyUpdate">{{ t("update.apply") }}</button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { appUpdate, applyUpdate, dismissUpdate } from "@/pwa/appUpdate";

const { t } = useI18n();
</script>

<style scoped>
.upd {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  z-index: 2400;
  display: flex;
  align-items: center;
  gap: 16px;
  max-width: min(560px, calc(100vw - 24px));
  padding: 12px 12px 12px 18px;
  border-radius: 10px;
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  border: 1px solid rgba(var(--v-border-color), 0.16);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.24);
}

.upd__text {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  line-height: 1.35;
}

.upd__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.upd__later,
.upd__apply {
  border: 0;
  border-radius: 7px;
  padding: 8px 14px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.upd__later {
  background: transparent;
  color: rgb(var(--v-theme-on-surface));
  opacity: 0.72;
}

.upd__later:hover {
  opacity: 1;
}

.upd__apply {
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
}

.upd__later:focus-visible,
.upd__apply:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.upd-enter-active,
.upd-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.upd-enter-from,
.upd-leave-to {
  opacity: 0;
  transform: translate(-50%, 12px);
}

@media (prefers-reduced-motion: reduce) {
  .upd-enter-active,
  .upd-leave-active {
    transition: none;
  }
}
</style>
